import { whyChooseUs } from "@/mocks/services";

interface WhyChooseUsProps {
  light?: boolean;
}

export default function WhyChooseUs({ light }: WhyChooseUsProps) {
  const sectionBg = light ? "bg-gray-50" : "bg-[#030c15]";
  const labelColor = light ? "text-[#0EA5E9]" : "text-[#7DD3FC]";
  const headingColor = light ? "text-gray-900" : "text-white";
  const bodyColor = light ? "text-gray-600" : "text-white";
  const cardBg = light
    ? "bg-white border-gray-200 hover:border-[#0EA5E9]/45 hover:shadow-[0_4px_24px_rgba(14,165,233,0.06)]"
    : "bg-[#071220] border-white/5 hover:border-[#0EA5E9]/25 hover:bg-[#071a2d]";
  const iconBg = "bg-[#0EA5E9]/10 group-hover:bg-[#0EA5E9]/18";
  const itemTitle = light ? "text-gray-900" : "text-white";
  const itemDesc = light ? "text-gray-500" : "text-white";

  return (
    <section className={`${sectionBg} py-28`}>
      <div className="max-w-7xl mx-auto px-6">

        {/* Top — Image + Copy side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-center mb-20">
          {/* Image — 2 columns */}
          <div className="lg:col-span-2 relative">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/4d9f6a7ca4ed80a8775bc8ca7596f9d2.jpg"
                alt="Knight In Shining Armor KISA Cooling — certified HVAC technician"
                className="w-full h-[420px] md:h-[480px] object-cover object-top"
              />
              {/* Dark gradient overlay bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {/* Floating badge */}
              <div className={`absolute bottom-6 left-6 right-6 flex items-center gap-4 p-4 rounded-xl backdrop-blur-md border ${light ? "bg-white/90 border-gray-200" : "bg-[#020B14]/80 border-white/12"}`}>
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#0EA5E9]/20 border border-[#0EA5E9]/40 flex-shrink-0">
                  <i className="ri-shield-check-fill text-[#38bdf8] text-lg"></i>
                </div>
                <div>
                  <div className={`font-['Inter'] ${light ? "text-gray-900" : "text-white"} font-bold text-sm`}>Licensed &amp; Insured</div>
                  <div className={`font-['Inter'] ${light ? "text-gray-500" : "text-white/85"} text-xs mt-0.5`}>
                    State-certified for all HVAC work in Texas
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative offset ring */}
            <div className="absolute -top-4 -left-4 w-20 h-20 rounded-full border border-[#0EA5E9]/12 pointer-events-none" />
            <div className="absolute -top-2 -left-2 w-12 h-12 rounded-full border border-[#0EA5E9]/18 pointer-events-none" />
          </div>

          {/* Copy — 3 columns */}
          <div className="lg:col-span-3">
            <p className={`font-['Inter'] ${labelColor} text-xs font-semibold uppercase tracking-[0.18em] mb-4`}>
              Why Knight In Shining Armor
            </p>
            <h2 className={`font-['Playfair_Display'] ${headingColor} text-4xl md:text-[2.75rem] font-bold mb-6 leading-[1.12]`}>
              Built for Reliability in Texas Heat
            </h2>
            <p className={`font-['Inter'] ${bodyColor} text-base leading-[1.9] mb-6`}>
              When temperatures push past 100°F, your cooling system isn&apos;t optional — it&apos;s critical. KISA delivers when it matters most, combining certified expertise with an unwavering commitment to long-term system performance.
            </p>
            <p className={`font-['Inter'] ${bodyColor} text-base leading-[1.9]`}>
              Every recommendation is transparent and honest. No overselling, no shortcuts. Just precision HVAC work that keeps your home comfortable, efficient, and running season after season.
            </p>
          </div>
        </div>

        {/* Benefits grid — 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {whyChooseUs.map((item) => (
            <div
              key={item.id}
              className={`flex gap-4 p-6 border rounded-2xl transition-all duration-300 cursor-default group ${cardBg}`}
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${iconBg} transition-all duration-300 flex-shrink-0 mt-0.5`}>
                <i className={`${item.icon} text-[#0EA5E9] text-lg`}></i>
              </div>
              <div>
                <h3 className={`font-['Inter'] ${itemTitle} font-bold text-[0.9rem] mb-1.5`}>{item.title}</h3>
                <p className={`font-['Inter'] ${itemDesc} text-xs leading-[1.75]`}>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
